/*
 * Racket Finder quiz — Prime Padel Shop
 * assets/racket-finder.js  (needs racket-lab.js loaded first, both with defer)
 *
 * INSTALL
 * 1. Online Store > Themes > "Racket Lab v3 2026-10-01 (new banner)" > ... > Duplicate.
 *    Rename the copy "Racket finder test YYYY-MM-DD". Work ONLY in the copy.
 * 2. In the copy: Edit code > add sections/racket-finder.liquid, sections/racket-finder-cta.liquid,
 *    assets/racket-finder.js, assets/racket-finder.css, templates/page.racket-finder.json and make the
 *    3 JSON template edits (index.json, collection.json, page.racket-lab.json).
 * 3. Online Store > Pages > Add page: title "Find your racket", handle racket-finder,
 *    template "page.racket-finder", content empty.
 *    SEO title: "Find Your Padel Racket | Racket Quiz | Prime Padel Shop"
 *    SEO description: "Answer 6 quick questions and get in-stock padel rackets matched to your level,
 *    play style, arm comfort and budget. Free US shipping over $199."
 * 4. Preview the copy at /pages/racket-finder?preview_theme_id=<copy id>. Run the test checklist.
 * 5. Content > Menus > "Main menu (NEW)" > RACKETS: add "FIND YOUR RACKET" -> Pages > Find your racket
 *    as the 2nd item (under "EXPLORE ALL", above "BRANDS"). Optional: "COMPARE RACKETS" -> /pages/racket-lab
 *    as the 3rd item. Menus are global, so do this right before publishing.
 * 6. Publish the copy only after the checklist passes. Keep the old theme as rollback.
 */
(function (global) {
  'use strict';

  /* ---------- All quiz copy (edit here). {b1} {b2} {b3} = budget band settings ---------- */
  const COPY = {
    stepOf: 'Question {n} of {total}',
    back: 'Back', next: 'Next', finish: 'Show my rackets', restart: 'Start over', edit: 'Edit answers',
    inStock: '{n} in stock',
    q: {
      who: { legend: 'Who is the padel racket for?', options: [
        ['adult', 'Me or another adult', 'Full-size padel rackets'],
        ['junior', 'A junior player', 'Kids’ padel rackets, about ages 6 to 12']] },
      level: { legend: 'How long have you been playing padel?', options: [
        ['beginner', 'I’m new to padel', 'Less than 6 months, or I play now and then'],
        ['intermediate', 'I play regularly', 'I can keep a rally going and I’m working on the glass and volleys'],
        ['advanced', 'I play competitively', 'Leagues or tournaments, 3+ times a week, confident with smashes']] },
      style: { legend: 'What should your racket help you with most?', options: [
        ['control', 'Control and consistency', 'Place the ball, keep it in play, make fewer errors'],
        ['versatile', 'A bit of everything', 'Balanced defense and attack'],
        ['attack', 'Power at the net', 'Smashes, volleys and finishing points']] },
      shape: { legend: 'Do you have a racket shape in mind?', options: [
        ['auto', 'Not sure, choose for me', 'We’ll pick the shape from your answers'],
        ['round', 'Round', 'Biggest sweet spot, most control and forgiveness'],
        ['teardrop', 'Teardrop (hybrid)', 'A mix of control and power'],
        ['diamond', 'Diamond', 'Most power, smaller sweet spot, for experienced players']],
        warn: 'Diamond padel rackets are hard to control when you’re starting out. We’ll show you the closest fit instead.' },
      arm: { legend: 'Any elbow, wrist or shoulder pain when you play?', options: [
        ['none', 'No, I’m fine'],
        ['sometimes', 'Sometimes, after long sessions'],
        ['now', 'Yes, I have pain right now']],
        help: 'We’ll favor softer, lighter, arm-friendly padel rackets. This isn’t medical advice.' },
      budget: { legend: 'What’s your budget?', options: [
        ['u150', 'Up to ${b1}'],
        ['150-250', '${b1} to ${b2}'],
        ['250-350', '${b2} to ${b3}'],
        ['350plus', '${b3} and up'],
        ['any', 'No limit, show me the best fit']] }
    },
    results: {
      heading: 'Your best matches',
      based: 'Based on your answers:',
      junior: 'Junior', autoShape: 'Shape picked for you', armFriendly: 'Arm-friendly',
      styles: { control: 'Control', versatile: 'All-round', attack: 'Power' },
      found: '{n} padel rackets found', foundOne: '1 padel racket found',
      loading: 'Finding your padel rackets…',
      best: 'Best match', also: 'Also a great fit', sale: 'Sale', regular: 'Regular price ',
      why: 'Why it fits you',
      specs: { weight: 'Weight', balance: 'Balance', carbon: 'Face' },
      add: 'Add to cart', adding: 'Adding…', added: 'Added to cart', details: 'View details',
      addAria: 'Add {name} to cart', cartError: 'Could not add to cart.',
      toast: '{name} added to cart', viewCart: 'View cart',
      compare: 'Compare these in the Racket Lab',
      empty: 'We couldn’t find an in-stock padel racket for these answers right now.',
      change: 'Change my answers', browse: 'Browse all padel rackets'
    },
    notes: {
      shape: 'No in-stock {shape} padel rackets matched everything, so we also looked at other shapes.',
      budget: 'Only {n} padel rackets matched your budget, so we added a few just outside it.',
      budgetOne: 'Only 1 padel racket matched your budget, so we added a few just outside it.',
      budgetNone: 'No padel rackets matched your budget, so we added a few just outside it.',
      level: 'We added padel rackets one level up that are still easy to play.',
      arm: 'Few arm-friendly padel rackets matched, so we included some with a firmer feel. Check weight and balance before you buy.'
    },
    why: {
      comfort: 'Arm-friendly: soft feel that cuts vibration',
      light: 'Light at {weight}, easy on the arm',
      level: { beginner: 'Easy to play while you learn', intermediate: 'Made for regular players', advanced: 'Built for competitive play' },
      style: { control: 'Built for control and consistency', versatile: 'Balanced for all-round play', attack: 'Built for power at the net' },
      shape: { round: 'Round shape: big sweet spot', teardrop: 'Teardrop shape: control plus power', diamond: 'Diamond shape: maximum power' }
    }
  };

  /* ====================================================================
   * 1. MATCHING (pure functions, no DOM)
   * ================================================================== */

  var ADULT_STEPS = ['who', 'level', 'style', 'shape', 'arm', 'budget'];
  var JUNIOR_STEPS = ['who', 'budget'];
  var VALID = {
    who: ['adult', 'junior'], level: ['beginner', 'intermediate', 'advanced'],
    style: ['control', 'versatile', 'attack'], shape: ['auto', 'round', 'teardrop', 'diamond'],
    arm: ['none', 'sometimes', 'now'], budget: ['u150', '150-250', '250-350', '350plus', 'any']
  };
  var NEIGHBOUR = { 'u150': '150-250', '150-250': '250-350', '250-350': '150-250', '350plus': '250-350' };
  var SHAPE_ORDER = ['round', 'teardrop', 'diamond'];
  var WEIGHTS = {
    none: { level: 30, style: 20, shape: 20, arm: 0, weight: 15, flat: 15 },
    sometimes: { level: 30, style: 15, shape: 15, arm: 25, weight: 15, flat: 0 },
    now: { level: 30, style: 10, shape: 10, arm: 35, weight: 15, flat: 0 }
  };
  var WINDOWS = { beginner: [0, 355], intermediate: [345, 365], advanced: [355, 375] };
  var COLOURS = /\b(mint|pink|red|blue|black|white|lilac|green|yellow|teal|purple|lime|gold|orange|grey|gray|silver)\b/g;

  function has(list, v) { return (list || []).indexOf(v) > -1; }
  function fill(str, map) { return String(str).replace(/\{(\w+)\}/g, function (m, k) { return map[k] == null ? m : map[k]; }); }

  function balanceOf(text) {
    if (/heavy/i.test(text)) return 'heavy';
    if (/light/i.test(text)) return 'light';
    if (/even/i.test(text)) return 'even';
    var m = /(\d{2,3}(?:[.,]\d)?)\s*(mm|cm)/i.exec(text);
    if (!m) return '';
    var cm = parseFloat(m[1].replace(',', '.'));
    if (m[2].toLowerCase() === 'mm') cm = cm / 10;
    return cm < 25.5 ? 'light' : cm < 26.5 ? 'even' : 'heavy';
  }

  /** Lab record (RacketLabParse.buildRacket) + the fields the quiz scores on. */
  function derive(raw, index, comfortHandles) {
    var r = global.RacketLabParse.buildRacket(raw);
    var tags = (raw.tags || []).map(function (t) { return String(t).toLowerCase().trim(); });
    var nums = (r.weight.match(/\d{3}/g) || []).map(Number).filter(function (n) { return n >= 250 && n <= 400; });
    r.index = index;
    r.v = (r.variants || []).filter(function (x) { return x.a === true; })[0] || null;   // in-stock rule
    r.priceC = r.v ? r.v.p : 0;
    r.weightG = nums.length ? nums.reduce(function (a, b) { return a + b; }, 0) / nums.length : null;
    r.bal = balanceOf(r.balance);
    r.softFace = /fiberglass|soft|flex/i.test(r.carbon);
    r.comfort = has(comfortHandles, r.handle) || has(tags, 'comfort') || /\b(soft|comfort|cloud|cmf)\b/i.test(r.title);
    r.junior = has(tags, 'junior') || /\bjunior\b|\bjr\b/i.test(r.title + ' ' + r.handle.replace(/-/g, ' '));
    r.family = r.title.replace(/padel\s+racket.*$/i, '').toLowerCase()
      .replace(/\b20\d\d\b/g, '').replace(COLOURS, '').replace(/\s+/g, ' ').trim();
    return r;
  }

  function bandOf(cents, bands) {
    return cents <= bands[0] ? 'u150' : cents <= bands[1] ? '150-250' : cents <= bands[2] ? '250-350' : '350plus';
  }

  /** Junior answers are fixed internally. */
  function normalize(a) {
    if (a.who !== 'junior') return a;
    return { who: 'junior', level: 'beginner', style: 'control', shape: 'auto', arm: 'none', budget: a.budget };
  }

  /** Returns clean answers when every required key is valid, else null. */
  function validate(a) {
    if (!a || !has(VALID.who, a.who)) return null;
    var out = {}, keys = a.who === 'junior' ? JUNIOR_STEPS : ADULT_STEPS;
    for (var i = 0; i < keys.length; i++) {
      if (!has(VALID[keys[i]], a[keys[i]])) return null;
      out[keys[i]] = a[keys[i]];
    }
    return out;
  }

  function targetShape(a, relaxed) {
    var t = a.shape !== 'auto' && !relaxed.shape ? a.shape
      : a.level === 'beginner' ? 'round'
      : { control: 'round', versatile: 'teardrop', attack: 'diamond' }[a.style];
    if (a.arm === 'now') t = SHAPE_ORDER[Math.max(0, SHAPE_ORDER.indexOf(t) - 1)];
    return t;
  }

  function passes(r, a, relaxed, bands, allowed) {
    var junior = a.who === 'junior';
    if (!r.v) return false;
    if (r.junior !== junior) return false;
    if (a.budget !== 'any' && !has(allowed, bandOf(r.priceC, bands))) return false;
    if (junior) return true;                         // junior rackets: stock, split and budget only
    var beg = has(r.levels, 'beginner'), mid = has(r.levels, 'intermediate');
    if (a.level === 'beginner') {
      if (!beg && !mid) return false;                // never relaxed
      if (r.shape === 'diamond') return false;       // never relaxed
      if (!beg && !relaxed.level) return false;
    }
    if (a.level === 'intermediate' && !mid && !relaxed.level) return false;
    if (a.arm === 'now' && !relaxed.arm) {
      if ((r.shape === 'diamond' || r.bal === 'heavy') && !r.comfort) return false;
      if (r.weightG > 365) return false;
    }
    return true;
  }

  function armScore(r) {
    return (r.comfort ? 40 : 0) + (r.softFace ? 20 : 0) +
      (r.bal === 'light' ? 20 : r.bal === 'even' ? 12 : 0) +
      (r.weightG == null ? 0 : r.weightG <= 355 ? 20 : r.weightG <= 365 ? 10 : 0);
  }

  function score(r, a, target) {
    var w = WEIGHTS[a.arm], L = r.levels, pts = w.flat;

    if (has(L, a.level)) pts += w.level;
    else if ((a.level === 'beginner' && has(L, 'intermediate')) ||
             (a.level === 'intermediate' && (has(L, 'beginner') || has(L, 'advanced'))) ||
             (a.level === 'advanced' && has(L, 'intermediate'))) pts += w.level / 2;

    if (has(r.styles, a.style)) pts += w.style;
    else if (has(r.styles, 'versatile') || a.style === 'versatile') pts += w.style / 2;

    var d = r.shape ? Math.abs(SHAPE_ORDER.indexOf(r.shape) - SHAPE_ORDER.indexOf(target)) : 9;
    pts += d === 0 ? w.shape : d === 1 ? w.shape / 2 : 0;

    if (w.arm) pts += w.arm * armScore(r) / 100;

    var win = WINDOWS[a.level], g = r.weightG;
    if (a.who === 'junior' || (g != null && g >= win[0] && g <= win[1])) pts += w.weight;
    else if (g == null || (g >= win[0] - 10 && g <= win[1] + 10)) pts += w.weight / 2;

    return Math.round(pts * 10) / 10;
  }

  /**
   * items: derived rackets in collection order. cfg: { bands: [c1, c2, c3], count }.
   * Returns { results, relaxed: [steps used], budgetBefore, target, answers }.
   */
  function match(items, answers, cfg) {
    var a = normalize(answers);
    var relaxed = { shape: false, budget: false, level: false, arm: false };
    var used = [], budgetBefore = null;
    var steps = a.who === 'junior' ? ['budget'] : ['shape', 'budget', 'level', 'arm'];
    var applies = { shape: a.shape !== 'auto', budget: a.budget !== 'any', level: a.level !== 'advanced', arm: a.arm === 'now' };

    function pass() {
      var allowed = [a.budget].concat(relaxed.budget ? [NEIGHBOUR[a.budget]] : []);
      var target = targetShape(a, relaxed), seen = {};
      return items.filter(function (r) { return passes(r, a, relaxed, cfg.bands, allowed); })
        .map(function (r) { r.score = score(r, a, target); return r; })
        .sort(function (x, y) { return y.score - x.score || x.index - y.index; })
        .filter(function (r) { if (seen[r.family]) return false; seen[r.family] = true; return true; });  // one colourway per model
    }

    var list = pass();
    for (var i = 0; i < steps.length && list.length < cfg.count; i++) {
      if (!applies[steps[i]]) continue;
      if (steps[i] === 'budget') budgetBefore = list.length;
      relaxed[steps[i]] = true;
      used.push(steps[i]);
      list = pass();
    }
    // copy the scores: `score` on the shared records is overwritten by the next run
    var top = list.slice(0, cfg.count);
    return { results: top, scores: top.map(function (r) { return r.score; }), relaxed: used, budgetBefore: budgetBefore, target: targetShape(a, relaxed), answers: a };
  }

  /** In-stock rackets per budget band for adults or juniors. */
  function budgetCounts(items, who, bands) {
    var out = { 'u150': 0, '150-250': 0, '250-350': 0, '350plus': 0 };
    items.forEach(function (r) { if (r.v && r.junior === (who === 'junior')) out[bandOf(r.priceC, bands)]++; });
    return out;
  }

  function reasons(r, a, target) {
    var out = [], W = COPY.why;
    if (a.arm !== 'none' && r.comfort) out.push(W.comfort);
    if (a.arm !== 'none' && r.weightG != null && r.weightG <= 355) out.push(fill(W.light, { weight: r.weight }));
    if (has(r.levels, a.level)) out.push(W.level[a.level]);
    if (has(r.styles, a.style)) out.push(W.style[a.style]);
    if (r.shape && r.shape === target) out.push(W.shape[r.shape]);
    return out.slice(0, 3);
  }

  global.RacketFinderCore = { COPY: COPY, derive: derive, match: match, validate: validate, budgetCounts: budgetCounts, reasons: reasons, balanceOf: balanceOf };
  if (typeof document === 'undefined') return;

  /* ====================================================================
   * 2. HELPERS
   * ================================================================== */

  var STORE_KEY = 'pps:racket-finder:v1';
  var CHECK = '<span class="rf-option__check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 6.2 5 8.6l4.5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function imgAt(url, w) {
    try { var u = new URL(url, location.origin); u.searchParams.set('width', w); return u.toString(); }
    catch (e) { return url; }
  }
  function root() { return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; }

  function track(name, data) {
    data = data || {};
    [
      function () { if (global.Shopify && global.Shopify.analytics && typeof global.Shopify.analytics.publish === 'function') global.Shopify.analytics.publish(name, data); },
      function () { if (typeof global.gtag === 'function') global.gtag('event', name, data); },
      function () { if (global.dataLayer) global.dataLayer.push(Object.assign({ event: name }, data)); },
      function () { document.dispatchEvent(new CustomEvent('racketfinder:' + name, { detail: data })); }
    ].forEach(function (fn) { try { fn(); } catch (e) {} });
  }

  function readSaved() {
    try {
      var s = JSON.parse(localStorage.getItem(STORE_KEY));
      if (s && s.v === 1 && Date.now() - s.ts < 30 * 864e5) return validate(s.answers);
    } catch (e) {}
    return null;
  }

  /* ====================================================================
   * 3. UI
   * ================================================================== */

  function Finder(el) {
    var self = this, d = function (k) { return el.getAttribute('data-' + k); };
    var cents = function (k, def) { var n = Math.round(parseFloat(d(k)) * 100); return n > 0 ? n : def; };
    this.el = el;
    this.cfg = {
      sectionId: d('section-id'),
      count: parseInt(d('results-count'), 10) || 3,
      bands: [cents('band-1', 15000), cents('band-2', 25000), cents('band-3', 35000)],
      showCounts: d('show-counts') !== 'false',
      auto: d('auto-advance') !== 'false',
      labUrl: d('lab-url') || '/pages/racket-lab',
      collectionUrl: d('collection-url') || '/collections/all-padel-racket'
    };
    this.$ = function (sel) { return el.querySelector(sel); };
    this.stage = this.$('[data-rf-stage]');
    if (!this.stage || !global.RacketLabParse) return;

    this.items = []; this.byHandle = {}; this.answers = {}; this.step = 'intro'; this.pending = 0;
    this.comfort = [];
    try { this.comfort = JSON.parse(this.$('[data-rf-comfort]').textContent) || []; } catch (e) {}
    try { this.ingest(JSON.parse(this.$('[data-rcl-data]').textContent)); }
    catch (e) { console.error('[Racket Finder] Could not read product data', e); }
    this.loadRemainingPages();
    this.bind();

    var ref = 'direct';
    try { if (document.referrer) ref = new URL(document.referrer).pathname; } catch (e) {}
    track('racket_finder_view', { source: ref });

    var last = this.$('[data-action="last-results"]');
    if (last && readSaved()) last.hidden = false;

    // Shared link: ?who=…&budget=… renders the results directly
    var q = new URLSearchParams(location.search), fromUrl = {};
    ADULT_STEPS.forEach(function (k) { if (q.get(k)) fromUrl[k] = q.get(k); });
    fromUrl = validate(fromUrl);
    if (fromUrl) { this.answers = fromUrl; this.finish(true); }
  }

  Finder.prototype.ingest = function (list) {
    var self = this;
    (list || []).forEach(function (raw) {
      if (self.byHandle[raw.handle]) return;
      var r = derive(raw, self.items.length, self.comfort);
      self.items.push(r); self.byHandle[r.handle] = r;
    });
  };

  /** Collections over 250 products: same Section Rendering pattern as the Racket Lab. */
  Finder.prototype.loadRemainingPages = function () {
    var self = this, total = parseInt(this.el.getAttribute('data-total-pages'), 10) || 1, pages = [];
    for (var p = 2; p <= total; p++) pages.push(p);
    if (!pages.length) return;
    this.pending = pages.length;
    pages.reduce(function (chain, page) {
      return chain.then(function () {
        return fetch(location.pathname + '?section_id=' + encodeURIComponent(self.cfg.sectionId) + '&page=' + page)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rcl-data]');
            if (node) self.ingest(JSON.parse(node.textContent));
          });
      });
    }, Promise.resolve())
      .catch(function (e) { console.warn('[Racket Finder] Extra pages failed', e); })
      .then(function () {
        self.pending = 0;
        if (self.step === 'results') self.finish(true);
        else if (self.step === 'budget') self.renderQuestion('budget', true);
      });
  };

  Finder.prototype.steps = function () { return this.answers.who === 'junior' ? JUNIOR_STEPS : ADULT_STEPS; };

  Finder.prototype.announce = function (msg) {
    var live = this.$('[data-rf-live]');
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 30);
  };

  Finder.prototype.setView = function (step) {
    this.step = step;
    clearTimeout(this._auto);
    var intro = this.$('[data-rf-intro]'), body = this.$('[data-rf-body]'), trust = this.$('[data-rf-trust]');
    if (intro) intro.hidden = step !== 'intro';
    if (body) body.hidden = step === 'intro';
    if (trust) trust.hidden = true;
    this.stage.classList.toggle('rf-stage--wide', step === 'results');
  };

  Finder.prototype.focusHeading = function () {
    var h = this.stage.querySelector('[data-rf-focus]');
    if (h) h.focus();
  };

  Finder.prototype.budgetLabel = function (value) {
    var b = this.cfg.bands, opt = COPY.q.budget.options.filter(function (o) { return o[0] === value; })[0];
    return fill(opt[1], { b1: b[0] / 100, b2: b[1] / 100, b3: b[2] / 100 });
  };

  /* ---------- questions ---------- */

  Finder.prototype.renderQuestion = function (key, keepFocus) {
    var self = this, steps = this.steps(), n = steps.indexOf(key) + 1, total = steps.length;
    var q = COPY.q[key], cur = this.answers[key], last = n === total;
    var counts = key === 'budget' && this.cfg.showCounts ? budgetCounts(this.items, this.answers.who, this.cfg.bands) : null;
    var label = fill(COPY.stepOf, { n: n, total: total });

    var options = q.options.map(function (o) {
      var sel = cur === o[0], title = key === 'budget' ? self.budgetLabel(o[0]) : o[1];
      return '<label class="rf-option' + (sel ? ' is-selected' : '') + '">' +
        '<input class="rf-option__input" type="radio" name="rf-' + key + '" value="' + o[0] + '"' + (sel ? ' checked' : '') + '>' +
        '<span class="rf-option__text"><span class="rf-option__title">' + esc(title) + '</span>' +
        (o[2] ? '<span class="rf-option__sub">' + esc(o[2]) + '</span>' : '') + '</span>' +
        (counts && counts[o[0]] != null ? '<span class="rf-count">' + fill(COPY.inStock, { n: counts[o[0]] }) + '</span>' : '') +
        CHECK + '</label>';
    }).join('');

    this.setView(key);
    this.stage.innerHTML =
      '<form class="rf-step" data-rf-form novalidate>' +
        '<p class="rf-step__count rcl-status">' + label + '</p>' +
        '<div class="rf-progress" role="progressbar" aria-label="Quiz progress" aria-valuemin="1" aria-valuemax="' + total + '" aria-valuenow="' + n + '">' +
          '<span class="rf-progress__bar" style="width:' + Math.round(n / total * 100) + '%"></span></div>' +
        '<fieldset class="rf-fieldset">' +
          '<legend class="rf-legend"><h2 class="rcl-h2" tabindex="-1" data-rf-focus>' + esc(q.legend) + '</h2></legend>' +
          '<div class="rf-options' + (key === 'budget' ? ' rf-options--single' : '') + '">' + options + '</div>' +
          (q.help ? '<p class="rf-help">' + esc(q.help) + '</p>' : '') +
          (q.warn ? '<p class="rf-note" role="status" data-rf-warn></p>' : '') +
        '</fieldset>' +
        '<div class="rf-nav">' +
          '<button type="button" class="rcl-btn rcl-btn--outline" data-action="back"' + (n === 1 ? ' hidden' : '') + '>' + COPY.back + '</button>' +
          '<button type="submit" class="rcl-btn rcl-btn--primary rf-nav__next"' + (cur ? '' : ' disabled') + '>' + (last ? COPY.finish : COPY.next) + '</button>' +
        '</div>' +
        '<p class="rf-restart"><button type="button" class="rcl-link" data-action="restart">' + COPY.restart + '</button></p>' +
      '</form>';
    this.updateWarn();
    if (!keepFocus) { this.focusHeading(); this.announce(label); }
  };

  Finder.prototype.updateWarn = function () {
    var warn = this.stage.querySelector('[data-rf-warn]');
    if (warn) warn.textContent = this.step === 'shape' && this.answers.level === 'beginner' && this.answers.shape === 'diamond' ? COPY.q.shape.warn : '';
  };

  Finder.prototype.advance = function () {
    var steps = this.steps(), i = steps.indexOf(this.step);
    if (i < 0 || !this.answers[this.step]) return;
    if (i === steps.length - 1) this.finish(false); else this.renderQuestion(steps[i + 1]);
  };

  Finder.prototype.autoNext = function () {
    var self = this, step = this.step;
    var warn = this.stage.querySelector('[data-rf-warn]');
    clearTimeout(this._auto);
    if (!this.cfg.auto || (warn && warn.textContent)) return;   // let the shopper read the note, then press Next
    this._auto = setTimeout(function () { if (self.step === step) self.advance(); }, 250);
  };

  /* ---------- results ---------- */

  Finder.prototype.finish = function (fromLink) {
    var a = validate(this.answers), R = COPY.results;
    if (!a) { this.renderQuestion('who'); return; }
    this.answers = a;
    this.setView('results');
    if (this.pending) { this.stage.innerHTML = '<p class="rcl-status" role="status">' + R.loading + '</p>'; return; }

    var out = match(this.items, a, this.cfg);
    this.out = out;
    var handles = out.results.map(function (r) { return r.handle; });

    // state: shareable URL (replaceState only) + last answers
    try {
      var u = new URL(location.href);
      ADULT_STEPS.forEach(function (k) { u.searchParams.delete(k); });
      Object.keys(a).forEach(function (k) { u.searchParams.set(k, a[k]); });
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (e) {}
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ v: 1, answers: a, ts: Date.now() })); } catch (e) {}

    if (handles.length) {
      track('racket_finder_complete', {
        who: a.who, level: out.answers.level, style: out.answers.style, shape: out.answers.shape, arm: out.answers.arm, budget: a.budget,
        results: handles, scores: out.scores, relaxed: out.relaxed
      });
    } else track('racket_finder_no_results', { answers: a });

    this.renderResults(out);
    this.focusHeading();
    this.announce(handles.length === 1 ? R.foundOne : fill(R.found, { n: handles.length }));
  };

  Finder.prototype.summaryHtml = function (out) {
    var a = out.answers, R = COPY.results, L = global.RacketLabParse.LABELS, parts;
    if (a.who === 'junior') parts = [R.junior];
    else {
      parts = [L[a.level], R.styles[a.style], a.shape === 'auto' ? R.autoShape : L[a.shape]];
      if (a.arm !== 'none') parts.push(R.armFriendly);
    }
    parts.push(this.budgetLabel(a.budget));
    return '<p class="rf-summary">' + R.based + ' ' + esc(parts.join(' · ')) +
      ' <button type="button" class="rcl-link" data-action="edit">' + COPY.edit + '</button>' +
      ' <button type="button" class="rcl-link" data-action="restart">' + COPY.restart + '</button></p>';
  };

  Finder.prototype.notesHtml = function (out) {
    var N = COPY.notes, L = global.RacketLabParse.LABELS, n = out.budgetBefore;
    return out.relaxed.map(function (step) {
      var text = step === 'shape' ? fill(N.shape, { shape: L[out.answers.shape] })
        : step === 'budget' ? (n === 0 ? N.budgetNone : n === 1 ? N.budgetOne : fill(N.budget, { n: n }))
        : N[step];
      return '<p class="rf-note">' + esc(text) + '</p>';
    }).join('');
  };

  Finder.prototype.cardHtml = function (r, i, out) {
    var R = COPY.results, P = global.RacketLabParse, full = (r.brand + ' ' + r.name).trim();
    var sale = r.v.c > r.v.p, why = reasons(r, out.answers, out.target);
    var chips = [r.shape ? P.LABELS[r.shape] : '', P.formatLevels(r.levels), P.formatStyles(r.styles)].filter(Boolean);
    var specs = ['weight', 'balance', 'carbon'].filter(function (k) { return r[k]; });
    var img = r.img
      ? '<img src="' + esc(imgAt(r.img, 500)) + '" srcset="' + esc([300, 400, 500, 700].map(function (w) { return imgAt(r.img, w) + ' ' + w + 'w'; }).join(', ')) +
        '" sizes="(min-width: 990px) 360px, 90vw" alt="' + esc(full) + ' padel racket" width="500" height="500" decoding="async"' +
        (i === 0 ? ' loading="eager" fetchpriority="high"' : ' loading="lazy"') + '>'
      : '';
    return '<li class="rf-card' + (i === 0 ? ' rf-card--best' : '') + '">' +
      '<div class="rf-card__media">' +
        '<span class="rf-card__badges"><span class="rcl-badge rf-badge' + (i === 0 ? ' rf-badge--best' : '') + '">' + (i === 0 ? R.best : R.also) + '</span>' +
        (sale ? '<span class="rcl-badge rf-badge rf-badge--sale">' + R.sale + '</span>' : '') + '</span>' +
        '<a href="' + esc(r.url) + '" tabindex="-1" aria-hidden="true" data-rf-pos="' + (i + 1) + '" data-handle="' + esc(r.handle) + '">' + img + '</a>' +
      '</div>' +
      '<div class="rf-card__body">' +
        '<p class="rf-card__brand">' + esc(r.brand) + '</p>' +
        '<h3 class="rf-card__name"><a href="' + esc(r.url) + '" data-rf-pos="' + (i + 1) + '" data-handle="' + esc(r.handle) + '">' + esc(r.name) + '</a></h3>' +
        '<p class="rf-card__chips">' + chips.map(function (c) { return '<span class="rcl-chip">' + esc(c) + '</span>'; }).join('') + '</p>' +
        '<p class="rf-card__price">' + (sale
          ? '<span class="rcl-price--sale">' + esc(r.v.pf) + '</span> <s class="rcl-price--was"><span class="rcl-visually-hidden">' + R.regular + '</span>' + esc(r.v.cf) + '</s>'
          : esc(r.v.pf)) + '</p>' +
        (why.length ? '<div class="rf-card__why"><h4>' + R.why + '</h4><ul>' + why.map(function (w) { return '<li>' + esc(w) + '</li>'; }).join('') + '</ul></div>' : '') +
        (specs.length ? '<div class="rf-card__specs">' + specs.map(function (k) { return '<dl class="rcl-spec"><dt>' + R.specs[k] + '</dt><dd>' + esc(r[k]) + '</dd></dl>'; }).join('') + '</div>' : '') +
        '<div class="rf-card__actions">' +
          '<button type="button" class="rcl-btn rcl-btn--primary" data-action="add-cart" data-handle="' + esc(r.handle) + '" data-rf-pos="' + (i + 1) + '" aria-label="' + esc(fill(R.addAria, { name: full })) + '">' + R.add + '</button>' +
          '<a class="rcl-btn rcl-btn--ghost" href="' + esc(r.url) + '" data-rf-pos="' + (i + 1) + '" data-handle="' + esc(r.handle) + '">' + R.details + '</a>' +
          '<p class="rf-card__msg" role="status"></p>' +
        '</div>' +
      '</div></li>';
  };

  Finder.prototype.renderResults = function (out) {
    var self = this, R = COPY.results, list = out.results, trust = this.$('[data-rf-trust]');
    var head = '<h2 class="rcl-h2" tabindex="-1" data-rf-focus>' + R.heading + '</h2>' + this.summaryHtml(out);
    if (!list.length) {
      this.stage.innerHTML = head + '<div class="rcl-grid-empty"><p>' + R.empty + '</p><div class="rf-empty__actions">' +
        '<button type="button" class="rcl-btn rcl-btn--outline" data-action="edit">' + R.change + '</button>' +
        '<a class="rcl-btn rcl-btn--primary" href="' + esc(this.cfg.collectionUrl) + '">' + R.browse + '</a></div></div>';
      return;
    }
    var lab = this.cfg.labUrl, compare = lab + (lab.indexOf('?') > -1 ? '&' : '?') + 'compare=' +
      list.map(function (r) { return encodeURIComponent(r.handle); }).join(',');
    this.stage.innerHTML = head + this.notesHtml(out) +
      '<ul class="rf-results rf-results--' + list.length + '" role="list">' + list.map(function (r, i) { return self.cardHtml(r, i, out); }).join('') + '</ul>' +
      (list.length > 1 ? '<p class="rf-compare"><a class="rcl-btn rcl-btn--outline" data-action="compare" href="' + esc(compare) + '">' + R.compare + '</a></p>' : '');
    if (trust) trust.hidden = false;
  };

  /* ---------- cart ---------- */

  Finder.prototype.addToCart = function (btn) {
    var self = this, R = COPY.results, r = this.byHandle[btn.getAttribute('data-handle')];
    if (!r || !r.v || btn.disabled) return;
    var msg = btn.parentNode.querySelector('.rf-card__msg');
    var state = function (cls, text, disabled) {
      btn.classList.remove('is-loading', 'is-added');
      if (cls) btn.classList.add(cls);
      btn.textContent = text; btn.disabled = disabled;
    };
    var fail = function (text) { state('', R.add, false); if (msg) msg.textContent = text || R.cartError; };
    if (msg) msg.textContent = '';
    state('is-loading', R.adding, true);

    var body = { items: [{ id: r.v.id, quantity: 1 }] }, sections = [];
    document.querySelectorAll('xo-cart-mini[id], xo-cart[id]').forEach(function (n) { if (sections.indexOf(n.id) < 0) sections.push(n.id); });
    if (sections.length) { body.sections = sections.slice(0, 5); body.sections_url = location.pathname; }

    // XHR on purpose: the theme wraps window.fetch and fires its cart toast for /cart/add,
    // which would show the toast twice next to our own showCartToast call below.
    var xhr = new XMLHttpRequest();
    xhr.open('POST', root() + 'cart/add.js');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.onerror = function () { fail(); };
    xhr.onload = function () {
      var data = {};
      try { data = JSON.parse(xhr.responseText) || {}; } catch (e) {}
      if (xhr.status < 200 || xhr.status >= 300 || data.status) { fail(data.description || data.message); return; }
      state('is-added', R.added, true);
      if (msg) msg.textContent = fill(R.toast, { name: (r.brand + ' ' + r.name).trim() });
      setTimeout(function () { state('', R.add, false); if (msg) msg.textContent = ''; }, 2200);
      track('racket_finder_add_to_cart', {
        handle: r.handle, variant_id: r.v.id, price: r.v.p / 100,
        position: parseInt(btn.getAttribute('data-rf-pos'), 10), currency: 'USD'
      });
      self.refreshThemeCart(data, r);
    };
    xhr.send(JSON.stringify(body));
  };

  /** Count bubble, theme store, theme toast, generic cart events. */
  Finder.prototype.refreshThemeCart = function (data, r) {
    var item = (data.items && data.items[0]) || data;
    var done = function (cart) {
      if (cart && global.xoStore && typeof global.xoStore.set === 'function') {
        try {
          var commit = global.xoStore.set('xo-cart', function (s) {
            s = s || {};
            var merged = {};
            Object.keys(s.sections || {}).forEach(function (k) { merged[k] = s.sections[k]; });
            Object.keys(data.sections || {}).forEach(function (k) { if (data.sections[k]) merged[k] = data.sections[k]; });
            return Object.assign({}, s, {
              size: cart.item_count, sections: merged, item: item, isAdded: true, addIdLoading: '',
              variantId: item.variant_id, productIdsForCartNotification: [String(item.product_id)]
            });
          });
          if (typeof commit === 'function') commit('racket-finder/addCartSuccess');
        } catch (e) { console.warn('[Racket Finder] xoStore update failed', e); }
      }
      if (typeof global.showCartToast === 'function') {
        try { global.showCartToast(item, 1, null, null); } catch (e) {}
      } else {
        var t = document.createElement('div');
        t.className = 'rcl-toast is-in';
        t.setAttribute('role', 'status');
        t.innerHTML = '<span>' + esc(fill(COPY.results.toast, { name: (r.brand + ' ' + r.name).trim() })) + '</span><a href="' + root() + 'cart">' + COPY.results.viewCart + '</a>';
        document.body.appendChild(t);
        setTimeout(function () { t.remove(); }, 4000);
      }
      if (!cart) return;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item } }));
      document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden="true"]').forEach(function (n) { n.textContent = cart.item_count; });
    };
    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(done, function () { done(null); });
  };

  /* ---------- events ---------- */

  Finder.prototype.restart = function () {
    var steps = this.steps(), i = steps.indexOf(this.step);
    track('racket_finder_restart', { from_step: i > -1 ? i + 1 : this.step });
    this.answers = {};
    try {
      var u = new URL(location.href);
      ADULT_STEPS.forEach(function (k) { u.searchParams.delete(k); });
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (e) {}
    this.setView('intro');
    this.stage.innerHTML = '';
    var start = this.$('[data-action="start"]');
    if (start) start.focus();
  };

  Finder.prototype.bind = function () {
    var self = this, el = this.el;

    el.addEventListener('click', function (e) {
      // a real tap/click on an option (keyboard changes have detail 0) may auto-advance
      var opt = e.target.closest('.rf-option');
      if (opt && e.detail > 0) {
        self._pointerAt = Date.now();
        var input = opt.querySelector('input');
        if (input && input.checked && self.answers[self.step] === input.value) self.autoNext();
      }

      var link = e.target.closest('a[data-rf-pos]');
      if (link) track('racket_finder_product_click', { handle: link.getAttribute('data-handle'), position: parseInt(link.getAttribute('data-rf-pos'), 10) });

      var btn = e.target.closest('[data-action]');
      if (!btn || !el.contains(btn)) return;
      var action = btn.getAttribute('data-action');
      if (action === 'start') { track('racket_finder_start', {}); self.renderQuestion('who'); }
      else if (action === 'back') { var s = self.steps(), i = s.indexOf(self.step); if (i > 0) self.renderQuestion(s[i - 1]); }
      else if (action === 'restart') self.restart();
      else if (action === 'edit') self.renderQuestion('who');
      else if (action === 'add-cart') self.addToCart(btn);
      else if (action === 'compare') track('racket_finder_compare_click', { handles: self.out ? self.out.results.map(function (r) { return r.handle; }) : [] });
      else if (action === 'last-results') { var saved = readSaved(); if (saved) { self.answers = saved; self.finish(true); } }
    });

    el.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.name || input.name.indexOf('rf-') !== 0 || input.name.slice(3) !== self.step) return;
      var key = self.step;
      self.answers[key] = input.value;
      self.stage.querySelectorAll('.rf-option').forEach(function (o) { o.classList.toggle('is-selected', o.contains(input)); });
      var next = self.stage.querySelector('.rf-nav__next');
      if (next) {
        next.disabled = false;
        var steps = self.steps();
        next.textContent = steps.indexOf(key) === steps.length - 1 ? COPY.finish : COPY.next;
      }
      self.updateWarn();
      track('racket_finder_answer', { question: key, answer: input.value, step: self.steps().indexOf(key) + 1 });
      if (Date.now() - (self._pointerAt || 0) < 600) self.autoNext();
    });

    el.addEventListener('submit', function (e) {
      if (!e.target.matches('[data-rf-form]')) return;
      e.preventDefault();
      self.advance();
    });
  };

  /* ---------- boot ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-racket-finder]').forEach(function (el) {
      if (!el.__racketFinder) el.__racketFinder = new Finder(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });   // theme editor
})(typeof window !== 'undefined' ? window : globalThis);
